import type { ToolCallMessagePartComponent } from '@assistant-ui/react';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { SkillInputFlow, SkillInputFlowReceipt } from './SkillInputFlow';
import {
  isConfigurePromptTemplateToolArgs,
  isPromptTemplateConfigureAnswersPayload,
  summarizeSkillAnswers,
  type ConfigurePromptTemplateToolArgs,
  type PromptTemplateConfigureAnswersPayload
} from './skillFlow.types';

function parseArgs(args: unknown): ConfigurePromptTemplateToolArgs | null {
  if (!isConfigurePromptTemplateToolArgs(args)) {
    return null;
  }
  return args;
}

function parseAnswersResult(
  result: unknown
): PromptTemplateConfigureAnswersPayload | null {
  if (isPromptTemplateConfigureAnswersPayload(result)) {
    return result;
  }
  if (typeof result !== 'string' || !result.trim()) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(result);
    return isPromptTemplateConfigureAnswersPayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Human-in-the-loop UI for collecting prompt template variables in chat. */
export const ConfigurePromptTemplateToolUI: ToolCallMessagePartComponent = ({
  args,
  result,
  addResult
}) => {
  const { data: userInfo } = useGetUserInfoQuery();
  const domains = userInfo?.data?.domains ?? [];

  const parsed = parseArgs(args);
  if (!parsed) {
    return (
      <div className="text-destructive text-xs">
        پیکربندی قالب پرامپت نامعتبر است.
      </div>
    );
  }

  const answers = parseAnswersResult(result);
  if (answers) {
    return (
      <SkillInputFlowReceipt
        id={parsed.id}
        title={parsed.title}
        summary={summarizeSkillAnswers(parsed.steps, answers.answers, {
          domains
        })}
      />
    );
  }

  if (result != null) {
    return (
      <SkillInputFlowReceipt
        id={parsed.id}
        title={parsed.title}
        summary={[{ label: 'وضعیت', value: 'در حال پردازش…' }]}
      />
    );
  }

  return (
    <SkillInputFlow
      id={parsed.id}
      title={parsed.title}
      description={parsed.description}
      steps={parsed.steps}
      onComplete={(answersPayload) => {
        addResult({
          phase: 'answers',
          promptTemplateId: parsed.promptTemplateId,
          answers: answersPayload
        });
      }}
    />
  );
};
